import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import MaltoraLogo from "../landing/MaltoraLogo";
import { useForm, Controller } from "react-hook-form";
import { AxiosError } from "axios";

import passCheckTick from "../../assets/passcheck-tick.svg";
import passCheckCross from "../../assets/passcheck-cross.svg";
import passwordIcon from "../../assets/landingPage/password-icon.svg";
import tickImage from "../../assets/landingPage/emailSentTick-icon.svg";

import { validatePassword } from "../../utils/validationUtils";
import { AuthApiService } from "../../service/authService";
import { resetPasswordWithBackend } from "../../api/loginAPI";
import { authFlowTypes } from "../../constants/authFlowTypes";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import { CustomButton, CustomInputField } from "../../common/FormElements";
import Toast from "../../common/Toast";
import {
  passwordValidationBoxStyle,
  getPasswordValidationTextStyle,
  updateButtonStyle,
} from "./SetNewPasswordScreen.styles";
import { routes } from "../../routes/routes";

const SetNewPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const flowTypes = searchParams.get("flowType") ?? "";
  const emailId =
    searchParams.get("useremail") ??
    searchParams.get("userName") ??
    searchParams.get("username") ??
    searchParams.get("userEmail") ??
    "";
  const tempPassword = searchParams.get("resetCode")
    ? (searchParams.get("resetCode") ?? "")
    : (location.href.split("code=")[1] ?? "");
  const orgId = localStorage.getItem("orgId");

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const passwordRulesStatus = useMemo(() => {
    return validatePassword(password);
  }, [password]);

  const triggerToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleAuthError = (error: unknown) => {
    let authError;

    if (error instanceof AxiosError && error.response?.data) {
      authError = AuthApiService.handleError(error.response.data);
    } else if (
      typeof error === "object" &&
      error !== null &&
      "code" in (error as any)
    ) {
      authError = AuthApiService.handleError(error);
    } else if (error instanceof Error) {
      authError = { message: error.message, code: 500, errCode: "" };
    } else {
      authError = {
        message: "An unexpected error occurred",
        code: 500,
        errCode: "",
      };
    }

    triggerToast(authError.message);
  };

  const onSubmit = async () => {
    const isPasswordValid = passwordRulesStatus.every((rule) => rule.isValid);
    setShowPasswordValidation(!isPasswordValid);
    if (!isPasswordValid) return;

    try {
      if (flowTypes === authFlowTypes.newUsers) {
        await authLogin();
      } else if (flowTypes === authFlowTypes.forgotPassword) {
        await authForgotPassword();
      } else {
        triggerToast("Invalid flow type. Please check the URL.");
        return;
      }

      navigate(`/${routes.public.signIn}`, {
        state: {
          image: tickImage,
          showButtons: true,
        },
      });
    } catch (error) {
      handleAuthError(error);
    }
  };

  const authLogin = async () => {
    try {
      const response = await AuthApiService.login(
        emailId,
        tempPassword,
        confirmPassword
      );
      if (!response.accessToken) {
        throw new Error("Login failed. Please try again.");
      }
    } catch (error) {
      handleAuthError(error);
    }
  };

  const authForgotPassword = async () => {
    try {
      // emailId is now fetched from userEmail param
      const response = await resetPasswordWithBackend(
        emailId,
        tempPassword,
        confirmPassword
      );
      if (!response) {
        throw new Error("Password reset failed. Please try again.");
      }
    } catch (error) {
      handleAuthError(error);
    }
  };

  const BENEFITS = [
    {
      title: "Digital Orders",
      desc: "Replace WhatsApp chaos with structured, trackable orders.",
      icon: (
        <>
          <path d="M9 11l3 3L22 4" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </>
      ),
    },
    {
      title: "Credit Control",
      desc: "Set limits, track outstanding, and automate reminders.",
      icon: (
        <>
          <rect x="1" y="4" width="22" height="16" rx="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </>
      ),
    },
    {
      title: "Live Inventory",
      desc: "Know your stock levels across every warehouse in real time.",
      icon: (
        <>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
          <line x1="12" y1="22.08" x2="12" y2="12" />
        </>
      ),
    },
    {
      title: "Full Visibility",
      desc: "One dashboard for your entire supply chain, end-to-end.",
      icon: (
        <>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </>
      ),
    },
  ];

  return (
    <>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error
      />

      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          fontFamily: "var(--font-ui, DM Sans, system-ui, sans-serif)",
        }}
      >
        {/* LEFT — green panel (hidden on mobile) */}
        {!isMobile && (
          <div
            style={{
              width: "420px",
              flexShrink: 0,
              background: "linear-gradient(160deg,#0F2D2B 0%,#1F5F5B 100%)",
              display: "flex",
              flexDirection: "column",
              padding: "48px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: "-80px",
                right: "-80px",
                width: "300px",
                height: "300px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.03)",
                pointerEvents: "none",
              }}
            />
            <Link
              to="/"
              style={{
                display: "block",
                width: "110px",
                marginBottom: "60px",
                flexShrink: 0,
              }}
            >
              <MaltoraLogo fill="#fff" />
            </Link>
            <h2
              style={{
                fontFamily:
                  "var(--font-display, Satoshi, DM Sans, system-ui, sans-serif)",
                fontSize: "26px",
                fontWeight: "700",
                color: "#fff",
                lineHeight: "1.3",
                letterSpacing: "-0.02em",
                marginBottom: "40px",
              }}
            >
              Everything your beverage
              <br />
              business needs,{" "}
              <em
                style={{ fontStyle: "italic", color: "rgba(255,255,255,0.6)" }}
              >
                in one place.
              </em>
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              {BENEFITS.map((b) => (
                <div
                  key={b.title}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "14px",
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.1)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="rgba(255,255,255,0.85)"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {b.icon}
                    </svg>
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#fff",
                        marginBottom: "2px",
                      }}
                    >
                      {b.title}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.55)",
                        lineHeight: "1.5",
                      }}
                    >
                      {b.desc}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RIGHT — form */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
            padding: isMobile ? "24px" : "40px 24px",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: "400px" }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 500,
                fontSize: isMobile ? "24px" : "28px",
                paddingBottom: "8px",
                letterSpacing: "-0.02em",
                color: "#0F172A",
              }}
            >
              New Password
            </Typography>
            <Typography
              sx={{ fontSize: "14px", paddingBottom: "32px", color: "#64748B" }}
            >
              Create your own password
            </Typography>

            {/* Password Field */}
            <Controller
              name="password"
              control={control}
              rules={{
                required: "Password is required",
                validate: () => {
                  const isValid = validatePassword(password).every(
                    (rule) => rule.isValid
                  );
                  setShowPasswordValidation(!isValid);
                  return isValid;
                },
              }}
              render={({ field }) => (
                <CustomInputField
                  label="New Password"
                  icon={
                    <img
                      src={passwordIcon}
                      alt="newPassword"
                      style={{ width: 20, height: 20 }}
                    />
                  }
                  placeholder="Enter your password"
                  type="password"
                  {...field}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  onBlur={() => trigger("password")}
                  labelSx={{ fontWeight: 400, fontSize: "12px" }}
                  inputSx={{ fontSize: "14px" }}
                />
              )}
            />

            {(showPasswordValidation || errors.password) && (
              <Box sx={passwordValidationBoxStyle}>
                {passwordRulesStatus.map((rule, index) => (
                  <Typography
                    key={index}
                    sx={getPasswordValidationTextStyle(rule.isValid)}
                  >
                    <img
                      src={rule.isValid ? passCheckTick : passCheckCross}
                      alt={rule.isValid ? "Valid" : "Invalid"}
                      style={{ width: 16, height: 16 }}
                    />
                    {rule.label}
                  </Typography>
                ))}
              </Box>
            )}

            {/* Confirm Password Field */}
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: "Please confirm your password",
                validate: (value) =>
                  value === password ||
                  "Confirm Password should be same as New Password",
              }}
              render={({ field }) => (
                <CustomInputField
                  label="Confirm Password"
                  icon={
                    <img
                      src={passwordIcon}
                      alt="confirmPassword"
                      style={{ width: 20, height: 20 }}
                    />
                  }
                  placeholder="Re-enter your password"
                  type="password"
                  {...field}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword?.message}
                  onBlur={() => trigger("confirmPassword")}
                  labelSx={{ fontWeight: 400, fontSize: "12px" }}
                  inputSx={{ fontSize: "14px" }}
                />
              )}
            />

            <CustomButton
              sx={updateButtonStyle}
              label="Update Password"
              variant="contained"
              onClick={handleSubmit(onSubmit)}
            />
          </Box>
        </div>
      </div>
    </>
  );
};

export default SetNewPasswordScreen;
